
import React, { useCallback, useContext, useState } from 'react';
import MoreHorizIcon from '@mui/icons-material//MoreHoriz';
import RefreshIcon from '@mui/icons-material//Refresh';
import { FinancialStatementPeriod } from 'common/types/financials';
import ValuationContext from 'pages/ValuationsAllocation/ValuationContext';
import { useGetPublicCompListBatchByDate } from 'services/hooks';
import { dbShortDate, formatDate } from 'utilities';
import { useRefreshHelpersWithContext } from './hooks';
import { GetPublicCompListBatchByDate, RefreshGPCOptionProps } from './RefreshGPCOption.props';
import {
  refreshGPCOptionClasses,
  RefreshGPCOptionMenuItem,
  RefreshGPCOptionProgress,
  RefreshGPCOptionRoot,
} from './RefreshGPCOption.styles';

const RefreshGPCOption = ({
  approach,
  approaches,
  setApproaches,
  closeApproachMenu,
  marketDate,
  marketCurrency,
}: RefreshGPCOptionProps) => {
  const gpcApproach = approach?.valuations_approach_gpc;
  const [isLoading, setIsLoading] = useState(false);

  const { financialsPeriods, valuation } = useContext(ValuationContext);

  const { getTickerListsByDate, getRefreshedApproaches } = useRefreshHelpersWithContext();
  const [, fetchGPCCompListsByDate]
    = useGetPublicCompListBatchByDate() as GetPublicCompListBatchByDate;

  const refreshGPCApproach = useCallback(async () => {
    if (isLoading || !financialsPeriods || !approach) {
      return;
    }
    setIsLoading(true);
    try {
      const tickerListsByDate = getTickerListsByDate(
        [approach],
        approaches,
        marketDate ? dbShortDate(marketDate) : undefined
      );
      // We await the fetch result so comps are applied only after an explicit "Refresh Data" action.
      const publicCompsDataByDate = await fetchGPCCompListsByDate?.(
        tickerListsByDate,
        marketCurrency,
        financialsPeriods as FinancialStatementPeriod[]
      );

      if (!publicCompsDataByDate) {
        return;
      }

      const refreshedApproaches = getRefreshedApproaches({
        publicCompsDataByDate,
        outdatedApproaches: [approach ?? {}],
        approaches,
        marketDate: marketDate ? dbShortDate(marketDate) : undefined,
      });

      closeApproachMenu();

      // This is for not lost the user introduced and not saved data when
      // the useEffect on src/pages/Valuations/Valuation.js try to syncronize
      // the valuation.valuations_approaches with the "approaches" state variable.
      // Actually we do the same when we add/remove an approach
      if (valuation) {
        valuation.valuations_approaches = [...refreshedApproaches];
      }

      // redraw approaches Accordion with the updated approach
      setApproaches([...refreshedApproaches]);
    } finally {
      setIsLoading(false);
    }
  }, [
    approach,
    approaches,
    isLoading,
    getTickerListsByDate,
    fetchGPCCompListsByDate,
    financialsPeriods,
    marketCurrency,
    marketDate,
    closeApproachMenu,
    getRefreshedApproaches,
    setApproaches,
    valuation,
  ]);

  return (
    <RefreshGPCOptionRoot>
      {isLoading && <RefreshGPCOptionProgress />}
      <RefreshGPCOptionMenuItem onClick={refreshGPCApproach}>
        <span className={refreshGPCOptionClasses.icon}>{isLoading ? <MoreHorizIcon /> : <RefreshIcon />}</span>
        <span className={refreshGPCOptionClasses.content}>
          <span className={refreshGPCOptionClasses.title}>{isLoading ? 'Refreshing' : 'Refresh Data'}</span>
          <span className={refreshGPCOptionClasses.meta}>
            Last refreshed {formatDate({ date: gpcApproach?.last_refresh_date ?? new Date().toUTCString() })}
          </span>
        </span>
      </RefreshGPCOptionMenuItem>
    </RefreshGPCOptionRoot>
  );
};

export default RefreshGPCOption;
